"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeft, Loader2 } from "lucide-react"
import { motion } from "framer-motion"
import { z } from "zod"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { FcGoogle } from "react-icons/fc"
import { SubmitHandler, useForm } from "react-hook-form"
import { Label } from "../../../components/landing/components/Fields"
import { Alert } from "../../../components/ui/Alert"
import { Button } from "../../../components/ui/button"
import { Logo } from "../../../components/ui/Logo"
import { AuthLayout } from "../../../layouts/AuthLayout"
import useAuth from "../../../hooks/useAuth"
import { signUpSchema } from "../../../validation/auth"

type SignUpFormValues = z.infer<typeof signUpSchema>

const inputClassName =
  "block w-full rounded-xl border border-gray-700 bg-gray-950/60 px-4 py-3 text-sm text-gray-100 placeholder-gray-500 shadow-inner transition-colors focus:border-emerald-500/80 focus:bg-gray-950 focus:outline-none focus:ring-2 focus:ring-emerald-500/25"

export default function SignUp() {
  const router = useRouter()
  const [loading, setLoading] = useState(false)
  const { signUpWithEmail, signInWithGoogle } = useAuth()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError
  } = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema)
  })

  const onSubmit: SubmitHandler<SignUpFormValues> = async (values) => {
    setLoading(true)
    try {
      const { user, error } = await signUpWithEmail(
        values.email,
        values.password
      )
      if (error) {
        setError("root", { type: "manual", message: error.message })
      } else if (user) {
        router.push("/auth/confirm-email")
      }
    } catch (error) {
      console.error("Sign-up error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    setLoading(true)
    try {
      const { error } = await signInWithGoogle()
      if (error) {
        setError("root", { type: "manual", message: error.message })
        setLoading(false)
      }
    } catch (error) {
      console.error("Google sign-up error:", error)
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="space-y-8"
      >
        <div>
          <Link
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 transition-colors hover:text-gray-200"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            {"Back to home"}
          </Link>
          <div className="mt-8 flex justify-center">
            <Link
              href="/"
              className="inline-flex w-full justify-center transition-opacity hover:opacity-90"
              aria-label="Seedmancer home"
            >
              <Logo variant="wordmark" />
            </Link>
          </div>
          <h1 className="mt-8 text-center text-2xl font-semibold tracking-tight text-gray-50">
            {"Create your account"}
          </h1>
          <p className="mt-2 text-center text-sm text-gray-400">
            {"Already have an account?"}{" "}
            <Link
              href="/auth/sign-in"
              className="font-medium text-emerald-400 transition-colors hover:text-emerald-300"
            >
              {"Sign in"}
            </Link>
          </p>
        </div>

        <div className="space-y-6">
          <Button
            type="button"
            variant="outline"
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border-gray-700 bg-white/[0.04] py-0 text-[15px] font-medium transition-colors hover:bg-white/[0.08]"
            onClick={handleGoogleSignUp}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : (
              <FcGoogle className="h-5 w-5" aria-hidden />
            )}
            <span>{"Continue with Google"}</span>
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center" aria-hidden>
              <div className="w-full border-t border-gray-700" />
            </div>
            <div className="relative flex justify-center text-xs font-medium uppercase tracking-wide">
              <span className="bg-gray-900 px-3 text-gray-400">{"or email"}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {errors.root && <Alert title={errors.root.message} />}

            <div className="space-y-1.5">
              <Label id="email">{"Email"}</Label>
              <input
                id="email"
                className={inputClassName}
                type="email"
                placeholder="you@example.com"
                autoComplete="email"
                {...register("email")}
              />
              {errors.email && (
                <p className="text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label id="password">{"Password"}</Label>
              <input
                id="password"
                className={inputClassName}
                type="password"
                placeholder="••••••••"
                autoComplete="new-password"
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-red-400">{errors.password.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label id="confirmPassword">{"Confirm password"}</Label>
              <input
                id="confirmPassword"
                className={inputClassName}
                type="password"
                placeholder="••••••••"
                autoComplete="new-password"
                {...register("confirmPassword")}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-red-400">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              variant="filled"
              className="mt-1 h-12 w-full rounded-xl text-[15px] font-semibold"
              disabled={isSubmitting || loading}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              ) : (
                "Create account"
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </AuthLayout>
  )
}
